import { ExternalLink, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

type Tool = {
  toolSetupId: string;
  name: string;
  note: string;
  link: string;
  ownerEmail: string;
  state: "pending" | "configured";
  lastAskedAt: string | null;
};

type Props = {
  tool: Tool;
  readOnly: boolean;
  onToggle: (toolSetupId: string) => void;
  onAskOwner: (toolSetupId: string) => void;
  asking: boolean;
};

const ASK_AGAIN_AFTER_MS = 24 * 60 * 60 * 1000; // BR-PORT-016

function askLabel(lastAskedAt: string | null): {
  label: string;
  variant: "primary" | "secondary";
} {
  if (!lastAskedAt) return { label: "Pedir ao responsável", variant: "primary" };
  const ts = Date.parse(lastAskedAt);
  if (Number.isNaN(ts) || Date.now() - ts >= ASK_AGAIN_AFTER_MS) {
    return { label: "Já pedido · pedir de novo?", variant: "secondary" };
  }
  return { label: "Pedido enviado", variant: "secondary" };
}

export function ToolRow({ tool, readOnly, onToggle, onAskOwner, asking }: Props) {
  const isConfigured = tool.state === "configured";
  const showAsk = !isConfigured && !!tool.ownerEmail; // BR-PORT-015
  const ask = askLabel(tool.lastAskedAt);

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border bg-card p-4",
        isConfigured && "bg-muted/30",
      )}
    >
      <Checkbox
        checked={isConfigured}
        disabled={readOnly}
        onCheckedChange={() => onToggle(tool.toolSetupId)}
        aria-label={isConfigured ? "Marcar como pendente" : "Marcar como configurado"}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "text-sm font-medium",
            isConfigured && "line-through text-muted-foreground",
          )}
        >
          {tool.name}
        </div>
        {tool.note && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{tool.note}</p>
        )}
        {tool.link && (
          <a
            href={tool.link}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            Abrir configuração
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {showAsk && !readOnly && (
        <Button
          type="button"
          variant={ask.variant === "primary" ? "default" : "outline"}
          size="sm"
          disabled={asking}
          onClick={() => onAskOwner(tool.toolSetupId)}
          className="shrink-0"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {ask.label}
        </Button>
      )}
    </div>
  );
}
